import { useEffect, useRef, useState } from 'react';
import { z, ZodError } from 'zod';
import { endpoints, type EndpointKey } from '@/api';
import {
  RequestType,
  ResponseSuccessType,
  ResponseErrorType,
  ApiError,
} from '@/types/endpointTypes';

import { getCached, invalidateCache, setCached } from '@/utils/cache';
import { hasPathParams, resolvePathParams } from '@/utils/pathParams';
import useRedirectToLogin from '@/hooks/useRedirectToLogin';
import useApiCache from '@/pages/ApiCache.context';

// Endpoints whose responses must never be served from the client cache.
// Matched as a substring of the resolved URL (the URL carries the
// `${API_URL_BASE}` prefix, so exact-equality against these bare paths would
// never hit — substring matching is intentional).
// recipient/me drives the access-page gate and is polled while identity
// verification completes out-of-band — a cached copy would stall the gate.
const cacheIgnoreUrls = [
  'users/me',
  'signings/my',
  'safe-transfers/recipient/me',
];

type LoadingType<T extends EndpointKey> =
  (typeof endpoints)[T]['method'] extends 'GET' ? boolean | 'update' : boolean;

const createErrorWrapper = (
  status: number,
  errorData: unknown
): ApiError<unknown> => {
  return {
    statusCode: status,
    response: errorData,
  };
};

const useApi = <T extends EndpointKey>(
  endpoint: T,
  newOptions: {
    omitRedirectToLogin?: boolean;
  } = {}
): [
  (
    requestData?: RequestType<T>
  ) => Promise<
    | { response: ResponseSuccessType<T>; error: null }
    | { response: null; error: ResponseErrorType<T> }
    | null
  >,
  {
    response: ResponseSuccessType<T> | null;
    loading: LoadingType<T>;
    error: ResponseErrorType<T> | null;
  },
] => {
  const options = {
    omitRedirectToLogin: false,
    ...newOptions,
  };
  const [response, setResponse] = useState<ResponseSuccessType<T> | null>(null);
  const [loading, setLoading] = useState<LoadingType<T>>(false);
  const [error, setError] = useState<ResponseErrorType<T> | null>(null);

  const [prevEndpoint, setPrevEndpoint] = useState(endpoint);

  if (prevEndpoint !== endpoint) {
    setPrevEndpoint(endpoint);
    setResponse(null);
    setError(null);
    setLoading(false);
  }

  const cache = useApiCache();
  const redirectToLogin = useRedirectToLogin();

  // The setState-during-render branch above causes the React Compiler to bail
  // out of memoizing this hook, so without help every render produces a new
  // `execute` identity — that breaks every consumer that depends on it in a
  // useEffect dep list. Stabilize via a ref + useState-initialized callback:
  // `execute` keeps the same identity for the lifetime of the hook and reads
  // the latest closure values from `latestRef`, which we sync after every
  // render.
  const latestRef = useRef({ endpoint, cache, redirectToLogin, options });
  useEffect(() => {
    latestRef.current = { endpoint, cache, redirectToLogin, options };
  });

  const [execute] = useState(() => {
    return async (requestData?: RequestType<T>) => {
      const { endpoint, cache, redirectToLogin, options } = latestRef.current;
      const {
        url,
        method,
        requestSchema,
        responseSuccessSchema,
        requiresToken,
        responseErrorSchemas,
      } = endpoints[endpoint];

      let resolvedUrl = url;
      let validatedBody;

      if (requestSchema && requestData) {
        try {
          validatedBody = requestSchema.parse(requestData);
        } catch (zodParseError) {
          if (import.meta.env.DEV) {
            // eslint-disable-next-line no-console
            console.error('zodParseError', zodParseError);
          }
        }

        if (
          hasPathParams(url) &&
          typeof validatedBody === 'object' &&
          !Array.isArray(validatedBody) &&
          validatedBody !== null
        ) {
          [resolvedUrl, validatedBody] = resolvePathParams<any>(
            url,
            validatedBody
          );
        }
      }

      let token = null;

      if (requiresToken === 'local') {
        token = localStorage.getItem('token');
      } else if (requiresToken === 'session') {
        token = sessionStorage.getItem('token');
      }

      const sudoToken = sessionStorage.getItem('sudoToken');

      if (
        method === 'GET' &&
        !cacheIgnoreUrls.some((ignore) => url.includes(ignore))
      ) {
        const cached = getCached(cache, resolvedUrl + token);
        if (cached) {
          setLoading('update' as LoadingType<T>);
          setResponse(cached as ResponseSuccessType<T>);
        } else {
          setLoading(true);
        }
      } else {
        setLoading(true);
      }

      const headers: {
        'Content-Type': string;
        Authorization?: string;
        'x-signing-verification-token'?: string;
      } = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      if (sudoToken) {
        headers['x-signing-verification-token'] = sudoToken;
      }

      const res = await fetch(resolvedUrl, {
        method,
        headers,
        body: validatedBody ? JSON.stringify(validatedBody) : undefined,
      });

      if (res.status === 401) {
        if (
          !window.location.pathname.startsWith('/auth/') &&
          !window.location.pathname.includes('/sign/') &&
          !options.omitRedirectToLogin
        ) {
          redirectToLogin(true);
        }
      } else if (res.status >= 300) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.error('useApi response status', res.status);
        }
      }

      let json = {} as Record<string | number | symbol, unknown>;

      try {
        json = (await res.json()) as Record<string | number | symbol, unknown>;
      } catch {
        // do nothing... ZOD will catch the error later on
      }

      if (res.status >= 200 && res.status < 300) {
        // All good, Response had happened. Only wrong thing could be ZOD
        try {
          const validatedResponse = responseSuccessSchema.parse(json);

          setResponse(validatedResponse);
          setError(null);

          if (method === 'GET') {
            setCached(cache, resolvedUrl + token, validatedResponse);
          }

          return {
            response: validatedResponse,
            error: null,
          };
        } catch (e) {
          if (import.meta.env.DEV && e instanceof ZodError) {
            // eslint-disable-next-line no-console
            console.error(
              `useApi. Success response schema validation failed for ${endpoint} (${url})`,
              e
            );
          }

          if (method === 'GET') {
            invalidateCache(cache, resolvedUrl + token);
          }

          // This is technically not 100% right, because we're not meeting ZOD schema. But we need a valid return value.
          return {
            response: json as ResponseSuccessType<T>,
            error: null,
          };
        } finally {
          setLoading(false);
        }
      } else if (responseErrorSchemas) {
        if (method === 'GET') {
          invalidateCache(cache, resolvedUrl + token);
        }

        try {
          (responseErrorSchemas as Record<string, z.ZodTypeAny>)[
            res.status.toString()
          ]?.parse(json);
          const errorWrapper = createErrorWrapper(res.status, json);
          setError(errorWrapper as ResponseErrorType<T>);
          return {
            response: null,
            error: errorWrapper as ResponseErrorType<T>,
          };
        } catch (errorValidation) {
          if (import.meta.env.DEV && errorValidation instanceof ZodError) {
            // eslint-disable-next-line no-console
            console.error(
              `useApi. Error response schema validation failed for ${endpoint} (${url})`,
              errorValidation
            );
          }
          const errorWrapper = createErrorWrapper(res.status, json);
          setError(errorWrapper as ResponseErrorType<T>);
          return {
            response: null,
            error: errorWrapper as ResponseErrorType<T>,
          };
        } finally {
          setLoading(false);
        }
      } else {
        if (method === 'GET') {
          invalidateCache(cache, resolvedUrl + token);
        }

        const errorWrapper = createErrorWrapper(res.status, json);
        setError(errorWrapper as ResponseErrorType<T>);
        setLoading(false);

        return {
          response: null,
          error: errorWrapper as ResponseErrorType<T>,
        };
      }
    };
  });

  return [execute, { response, error, loading }];
};

export default useApi;
